import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, ChevronRight, Car, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "./Badge";
import {
  isRequestToBookCategory,
  RequestToBookBadge,
} from "./RequestToBookBadge";

export interface VehicleCategorySelection {
  type: "category" | "vehicle";
  categoryId: string;
  categoryName: string;
  vehicleId?: string;
  vehicleName?: string;
}

export interface VehicleCategorySelectorProps {
  label?: string;
  categories: Array<{
    id: string;
    name: string;
    /** The category's own mode; `request` may show the badge. */
    booking_mode?: "instant" | "request" | null;
    vehicles: Array<{
      id: string;
      name: string;
      make?: string;
      model?: string;
      booking_mode?: "instant" | "request" | null;
      booking_mode_override?: "instant" | "request" | null;
    }>;
    vehicle_count?: number;
  }>;
  value?: VehicleCategorySelection | null;
  onChange: (selection: VehicleCategorySelection | null) => void;
  placeholder?: string;
  error?: string;
  helpText?: string;
  id?: string;
  className?: string;
  showLabel?: boolean;
  showWarning?: boolean;
  disabled?: boolean;
  uncategorizedVehicles?: Array<{
    id: string;
    name: string;
    make?: string;
    model?: string;
    booking_mode?: "instant" | "request" | null;
    booking_mode_override?: "instant" | "request" | null;
  }>;
}

export function VehicleCategorySelector({
  label,
  categories,
  value,
  onChange,
  placeholder = "Select a category or vehicle",
  error,
  helpText,
  id,
  className,
  showLabel = true,
  disabled = false,
  uncategorizedVehicles = [],
}: VehicleCategorySelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [expandedCategories, setExpandedCategories] = React.useState<
    Set<string>
  >(new Set(categories.map((cat) => cat.id)));

  // Filter categories and vehicles based on search term
  const filteredData = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return categories;
    }

    const searchLower = searchTerm.toLowerCase();
    return categories
      .map((category) => {
        // Check if category name matches (guard against unexpected nulls)
        const categoryMatches = (category.name || "")
          .toLowerCase()
          .includes(searchLower);

        // Filter vehicles that match search term (guard against undefined fields)
        const matchingVehicles = (category.vehicles || []).filter(
          (vehicle: { name: string; make?: string; model?: string }) => {
            const name = (vehicle.name || "").toLowerCase();
            const make = (vehicle.make || "").toLowerCase();
            const model = (vehicle.model || "").toLowerCase();
            return (
              name.includes(searchLower) ||
              make.includes(searchLower) ||
              model.includes(searchLower)
            );
          },
        );

        // Include category if either category name matches OR has matching vehicles
        if (categoryMatches || matchingVehicles.length > 0) {
          return {
            ...category,
            vehicles: matchingVehicles,
          };
        }

        return null;
      })
      .filter(Boolean) as Array<{
      id: string;
      name: string;
      booking_mode?: "instant" | "request" | null;
      vehicles: Array<{
        id: string;
        name: string;
        make?: string;
        model?: string;
        booking_mode?: "instant" | "request" | null;
        booking_mode_override?: "instant" | "request" | null;
      }>;
      vehicle_count?: number;
    }>;
  }, [categories, searchTerm]);

  // Filter uncategorized vehicles based on search term
  const filteredUncategorizedVehicles = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return uncategorizedVehicles;
    }

    const searchLower = searchTerm.toLowerCase();
    return uncategorizedVehicles.filter((vehicle) => {
      const name = (vehicle.name || "").toLowerCase();
      const make = (vehicle.make || "").toLowerCase();
      const model = (vehicle.model || "").toLowerCase();
      return (
        name.includes(searchLower) ||
        make.includes(searchLower) ||
        model.includes(searchLower)
      );
    });
  }, [uncategorizedVehicles, searchTerm]);

  // Auto-expand categories when searching, but keep all expanded by default
  React.useEffect(() => {
    if (searchTerm.trim()) {
      const allCategoryIds = new Set(filteredData.map((cat) => cat.id));
      setExpandedCategories(allCategoryIds);
    } else {
      // Keep all categories expanded when not searching
      setExpandedCategories(new Set(categories.map((cat) => cat.id)));
    }
  }, [searchTerm, filteredData, categories]);

  const handleCategorySelect = (category: { id: string; name: string }) => {
    const selection: VehicleCategorySelection = {
      type: "category",
      categoryId: category.id,
      categoryName: category.name,
    };
    onChange(selection);
    setOpen(false);
    setSearchTerm("");
  };

  const handleVehicleSelect = (
    category: { id: string; name: string },
    vehicle: { id: string; name: string },
  ) => {
    const selection: VehicleCategorySelection = {
      type: "vehicle",
      categoryId: category.id,
      categoryName: category.name,
      vehicleId: vehicle.id,
      vehicleName: vehicle.name,
    };
    onChange(selection);
    setOpen(false);
    setSearchTerm("");
  };

  const handleUncategorizedVehicleSelect = (vehicle: {
    id: string;
    name: string;
  }) => {
    const selection: VehicleCategorySelection = {
      type: "vehicle",
      categoryId: "",
      categoryName: "",
      vehicleId: vehicle.id,
      vehicleName: vehicle.name,
    };
    onChange(selection);
    setOpen(false);
    setSearchTerm("");
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const getDisplayText = () => {
    if (!value) return placeholder;

    if (value.type === "category") {
      return value.categoryName;
    } else {
      return value.vehicleName;
    }
  };

  const getCategoryIcon = () => {
    return <Car className="h-4 w-4 text-blue-600" />;
  };

  const getVehicleIcon = () => {
    return <Car className="h-3 w-3 text-green-600" />;
  };

  return (
    <div className={cn("space-y-1", className)}>
      {showLabel && label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 hover:border-gray-400 transition-colors",
              !value && "text-gray-500",
              error && "border-red-500 focus:ring-red-500",
            )}
            id={id}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate text-left">{getDisplayText()}</span>
            </div>
            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="mx-6 rounded-lg border border-gray-200 bg-white shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
            style={{ zIndex: 9999 }}
            sideOffset={4}
          >
            <div className="p-0">
              {/* Search - Fixed at top */}
              <div className="p-3 border-b border-gray-100 bg-gray-50 sticky top-0 z-10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search categories and vehicles..."
                    value={searchTerm}
                    autoComplete="do-not-autofill"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                </div>
              </div>

              {/* Options - Scrollable content */}
              <div className="max-h-80 overflow-y-auto">
                {filteredData.length === 0 &&
                filteredUncategorizedVehicles.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No categories or vehicles found
                  </div>
                ) : (
                  <>
                    {filteredData.map((category) => (
                      <div
                        key={category.id}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        {/* Category Row */}
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => toggleCategoryExpansion(category.id)}
                            className="p-3 hover:bg-gray-50 flex items-center justify-center transition-colors"
                          >
                            {category.vehicles.length > 0 ? (
                              expandedCategories.has(category.id) ? (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-500" />
                              )
                            ) : (
                              <div className="w-4 h-4" />
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleCategorySelect(category)}
                            className="flex-1 flex items-center p-3 hover:bg-gray-50 text-left transition-colors"
                          >
                            {getCategoryIcon()}
                            <span className="ml-3 font-medium text-gray-900">
                              {category.name}
                            </span>
                            {isRequestToBookCategory(category) && (
                              <RequestToBookBadge className="ml-2" />
                            )}
                            <Badge
                              variant="secondary"
                              className="ml-auto text-xs"
                            >
                              {category.vehicle_count ||
                                category.vehicles.length}
                            </Badge>
                          </button>
                        </div>

                        {/* Vehicles */}
                        {expandedCategories.has(category.id) &&
                          category.vehicles.length > 0 && (
                            <div className="bg-gray-50/50">
                              {category.vehicles.map(
                                (vehicle: {
                                  id: string;
                                  name: string;
                                  make?: string;
                                  model?: string;
                                  booking_mode?: "instant" | "request" | null;
                                  booking_mode_override?:
                                    | "instant"
                                    | "request"
                                    | null;
                                }) => (
                                  <button
                                    key={vehicle.id}
                                    type="button"
                                    onClick={() =>
                                      handleVehicleSelect(category, vehicle)
                                    }
                                    className="w-full flex items-center pl-12 pr-4 py-3 hover:bg-gray-100 text-left transition-colors"
                                  >
                                    {getVehicleIcon()}
                                    <span className="ml-3 text-gray-900">
                                      {vehicle.name}
                                    </span>
                                    {vehicle.booking_mode === "request" && (
                                      <RequestToBookBadge className="ml-2" />
                                    )}
                                    {vehicle.make && vehicle.model && (
                                      <span className="ml-2 text-sm text-gray-500">
                                        ({vehicle.make} {vehicle.model})
                                      </span>
                                    )}
                                  </button>
                                ),
                              )}
                            </div>
                          )}
                      </div>
                    ))}

                    {/* Uncategorized Vehicles */}
                    {filteredUncategorizedVehicles.length > 0 && (
                      <div className="border-t border-gray-200">
                        <div className="px-4 py-2 bg-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Other Vehicles
                        </div>
                        {filteredUncategorizedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() =>
                              handleUncategorizedVehicleSelect(vehicle)
                            }
                            className="w-full flex items-center px-4 py-3 hover:bg-gray-100 text-left transition-colors"
                          >
                            {getVehicleIcon()}
                            <span className="ml-3 text-gray-900">
                              {vehicle.name}
                            </span>
                            {vehicle.make && vehicle.model && (
                              <span className="ml-2 text-sm text-gray-500">
                                ({vehicle.make} {vehicle.model})
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Clear Selection - Fixed at bottom */}
              {value && (
                <div className="p-3 border-t border-gray-100 bg-gray-50 sticky bottom-0 z-10">
                  <button
                    type="button"
                    onClick={() => {
                      onChange(null);
                      setOpen(false);
                      setSearchTerm("");
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
            <Popover.Arrow className="fill-white" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {helpText && <p className="text-sm text-gray-500">{helpText}</p>}
    </div>
  );
}
